import clsx from 'clsx';
import type { ReactNode, Ref } from 'react';

import '@repo/sds/components/SDSFlexbox/SDSFlexbox.scss';

export type SDSFlexboxDirection = 'row' | 'column';
export type SDSFlexboxJustifyContent = 'center' | 'space-between' | 'space-around' | 'flex-end' | 'flex-start';
export type SDSFlexboxAlignItems = 'center' | 'flex-end' | 'flex-start' | 'stretch' | 'baseline';
export type SDSFlexboxWrap = 'wrap' | 'nowrap';

type SDSTextFlexboxGap = 'text-small' | 'text-medium' | 'text-large' | 'text-x-large' | 'text-xx-large';
type SDSCardFlexboxGap = 'card-medium' | 'card-large';
type SDSLandingFlexboxGap = 'landing-big' | 'landing-cards' | 'landing-blocks';
type SDSButtonFlexboxGap =
  | 'button-small'
  | 'button-regular'
  | 'button-medium'
  | 'button-large'
  | 'button-x-large'
  | 'button-xx-large';
type SDSContentFlexboxGap = 'content-default' | 'content-large' | 'content-x-large';
type SDSIconFlexboxGap = 'icon-icon';
type SDSInputFlexboxGap =
  | 'input-small'
  | 'input-regular'
  | 'input-medium'
  | 'input-large'
  | 'input-x-large'
  | 'input-xx-large';

// Temporary fallback if no design is available
type SDSNoDesignFlexboxGap =
  | 'no-design-4'
  | 'no-design-8'
  | 'no-design-12'
  | 'no-design-16'
  | 'no-design-20'
  | 'no-design-24'
  | 'no-design-28'
  | 'no-design-32';

export type SDSFlexboxGap =
  | SDSTextFlexboxGap
  | SDSCardFlexboxGap
  | SDSLandingFlexboxGap
  | SDSButtonFlexboxGap
  | SDSContentFlexboxGap
  | SDSIconFlexboxGap
  | SDSInputFlexboxGap
  | SDSNoDesignFlexboxGap;

export interface SDSFlexboxProps {
  id?: string;
  children: ReactNode;
  direction?: SDSFlexboxDirection;
  justifyContent?: SDSFlexboxJustifyContent;
  alignItems?: SDSFlexboxAlignItems;
  wrap?: SDSFlexboxWrap;
  gap?: SDSFlexboxGap;
  fullWidth?: boolean;
  fullHeight?: boolean;
  overflowHidden?: boolean;
  className?: string;
  style?: React.CSSProperties;
  dataTestId?: string;
  ref?: Ref<HTMLDivElement>;
}

export function SDSFlexbox({
  id,
  children,
  direction = 'row',
  justifyContent,
  alignItems,
  wrap,
  gap,
  fullWidth,
  fullHeight,
  overflowHidden,
  className,
  style,
  dataTestId,
  ref,
}: SDSFlexboxProps) {
  return (
    <div
      id={id}
      ref={ref}
      className={clsx(
        'SDSFlexbox',
        {
          [`SDSFlexbox--${direction}`]: direction,
          [`SDSFlexbox--justify-${justifyContent}`]: justifyContent,
          [`SDSFlexbox--align-${alignItems}`]: alignItems,
          [`SDSFlexbox--wrap-${wrap}`]: wrap,
          [`SDSFlexbox--gap-${gap}`]: gap,
          'SDSFlexbox--full-width': fullWidth,
          'SDSFlexbox--full-height': fullHeight,
          'SDSFlexbox--overflow-hidden': overflowHidden,
        },
        className,
      )}
      style={style}
      data-testid={dataTestId}
    >
      {children}
    </div>
  );
}
